/**
 * Copie de travail locale, reprise de session et verrou — EF-FIC-001,
 * EF-FIC-003, EF-MOD-008.
 *
 * Cette copie n'est pas un fichier transférable et ne dispense jamais d'un
 * export.
 */

import { newId } from '../core/ids';
import type { Poi, PoiId, Results, RoadbookDocument, SelectedElement } from '../core/model';
import type { Parameters } from '../core/parameters';
import { rehydrateComputedTrack, type ComputedTrack, type DistanceAnomaly, type ElevationSummary } from '../geo/track';
import type { SearchState } from '../overpass/search';
import { clearStores, openDatabase, readAll, readValue, runTransaction, SINGLETON_KEY, STORES, writeValue } from './database';

/** Période de rafraîchissement du verrou, en millisecondes. */
export const LOCK_REFRESH_MS = 10_000;

/** Délai au-delà duquel un verrou est considéré abandonné, en millisecondes. */
export const LOCK_EXPIRY_MS = 30_000;

/** Regroupement des modifications successives, en millisecondes. */
export const SAVE_COALESCE_MS = 5000;

export interface StoredMeta {
  readonly id: string;
  readonly title: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly savedAt: string;
}

export interface StoredRoute {
  readonly sourceFileName: string | null;
  readonly selectedElement: SelectedElement;
  readonly originalPointCount: number;
  readonly lat: Float64Array;
  readonly lon: Float64Array;
  readonly alt: Float64Array;
  readonly count: number;
  readonly lengthMeters: number;
  readonly segmentGaps: readonly DistanceAnomaly[];
  readonly pointAnomalies: readonly DistanceAnomaly[];
  readonly elevation: ElevationSummary;
  readonly elevationGainMeters: number | null;
}

export interface StoredLock {
  readonly contextId: string;
  readonly refreshedAt: number;
}

export interface SessionSnapshot {
  readonly document: RoadbookDocument;
  readonly parameters: Parameters;
  readonly savedAt: string;
  readonly searchState: SearchState | null;
}

function toStoredRoute(document: RoadbookDocument): StoredRoute {
  const track = document.route.track;
  return {
    sourceFileName: document.route.sourceFileName,
    selectedElement: document.route.selectedElement,
    originalPointCount: document.route.originalPointCount,
    // Les tableaux cumulés et l'index spatial ne sont jamais enregistrés : leur
    // reconstruction à l'ouverture coûte moins que leur sérialisation.
    lat: track.lat,
    lon: track.lon,
    alt: track.alt,
    count: track.count,
    lengthMeters: track.lengthMeters,
    segmentGaps: track.segmentGaps,
    pointAnomalies: track.pointAnomalies,
    elevation: track.elevation,
    elevationGainMeters: track.elevationGainMeters,
  };
}

function fromStoredRoute(stored: StoredRoute): ComputedTrack {
  return rehydrateComputedTrack({
    lat: stored.lat,
    lon: stored.lon,
    alt: stored.alt,
    count: stored.count,
    lengthMeters: stored.lengthMeters,
    segmentGaps: stored.segmentGaps,
    pointAnomalies: stored.pointAnomalies,
    elevation: stored.elevation,
    elevationGainMeters: stored.elevationGainMeters,
  });
}

/** Accès à la copie de travail locale. */
export class SessionStore {
  private constructor(private readonly database: IDBDatabase) {}

  static async open(databaseName?: string): Promise<SessionStore> {
    return new SessionStore(await openDatabase(databaseName));
  }

  close(): void {
    this.database.close();
  }

  /**
   * Écrit l'intégralité du document. Réservé à l'importation d'un parcours ou
   * d'un roadbook : les gestes courants passent par les écritures partielles.
   */
  async saveAll(document: RoadbookDocument, parameters: Parameters): Promise<void> {
    const savedAt = new Date().toISOString();
    await runTransaction(
      this.database,
      [STORES.meta, STORES.route, STORES.pois, STORES.results, STORES.settings],
      'readwrite',
      (transaction) => {
        const meta: StoredMeta = {
          id: document.id,
          title: document.title,
          createdAt: document.createdAt,
          updatedAt: document.updatedAt,
          savedAt,
        };
        transaction.objectStore(STORES.meta).put(meta, SINGLETON_KEY);
        transaction.objectStore(STORES.route).put(toStoredRoute(document), SINGLETON_KEY);
        transaction.objectStore(STORES.results).put(document.results, SINGLETON_KEY);
        transaction.objectStore(STORES.settings).put(parameters, 'parameters');

        const pois = transaction.objectStore(STORES.pois);
        pois.clear();
        for (const poi of document.pois.values()) pois.put(poi);
      },
    );
  }

  /** Réécrit les seuls POI modifiés, avec la méta et les résultats. */
  async savePartial(
    document: RoadbookDocument,
    changedPoiIds: readonly PoiId[],
    removedPoiIds: readonly PoiId[] = [],
  ): Promise<void> {
    const savedAt = new Date().toISOString();
    await runTransaction(this.database, [STORES.meta, STORES.pois, STORES.results], 'readwrite', (transaction) => {
      const meta: StoredMeta = {
        id: document.id,
        title: document.title,
        createdAt: document.createdAt,
        updatedAt: document.updatedAt,
        savedAt,
      };
      transaction.objectStore(STORES.meta).put(meta, SINGLETON_KEY);
      transaction.objectStore(STORES.results).put(document.results, SINGLETON_KEY);

      const pois = transaction.objectStore(STORES.pois);
      for (const id of changedPoiIds) {
        const poi = document.pois.get(id);
        if (poi !== undefined) pois.put(poi);
      }
      for (const id of removedPoiIds) pois.delete(id);
    });
  }

  async saveParameters(parameters: Parameters): Promise<void> {
    await writeValue(this.database, STORES.settings, parameters, 'parameters');
  }

  async readSetting<T>(name: string): Promise<T | null> {
    return readValue<T>(this.database, STORES.settings, name);
  }

  async writeSetting(name: string, value: unknown): Promise<void> {
    await writeValue(this.database, STORES.settings, value, name);
  }

  async saveSearchState(state: SearchState | null): Promise<void> {
    if (state === null) {
      await clearStores(this.database, [STORES.searchState]);
      return;
    }
    await writeValue(this.database, STORES.searchState, state, SINGLETON_KEY);
  }

  async readSearchState(): Promise<SearchState | null> {
    return readValue<SearchState>(this.database, STORES.searchState, SINGLETON_KEY);
  }

  /** Restitue la session enregistrée, ou `null` en l'absence de sauvegarde. */
  async load(): Promise<SessionSnapshot | null> {
    const meta = await readValue<StoredMeta>(this.database, STORES.meta, SINGLETON_KEY);
    const route = await readValue<StoredRoute>(this.database, STORES.route, SINGLETON_KEY);
    if (meta === null || route === null) return null;

    const results = await readValue<Results>(this.database, STORES.results, SINGLETON_KEY);
    const parameters = await readValue<Parameters>(this.database, STORES.settings, 'parameters');
    const poiRecords = await readAll<Poi>(this.database, STORES.pois);
    const searchState = await this.readSearchState();

    if (results === null || parameters === null) return null;

    const document: RoadbookDocument = {
      id: meta.id,
      title: meta.title,
      createdAt: meta.createdAt,
      updatedAt: meta.updatedAt,
      route: {
        sourceFileName: route.sourceFileName,
        selectedElement: route.selectedElement,
        originalPointCount: route.originalPointCount,
        track: fromStoredRoute(route),
      },
      pois: new Map(poiRecords.map((poi) => [poi.id, poi])),
      results,
    };

    return { document, parameters, savedAt: meta.savedAt, searchState };
  }

  // -------------------------------------------------------------------------
  // Fenêtre d'annulation de la purge — EF-POI-016
  // -------------------------------------------------------------------------

  /**
   * Place les POI purgés en attente. Ils disparaissent immédiatement de la
   * liste, mais leur suppression n'est rendue définitive qu'à la fermeture de
   * la fenêtre d'annulation.
   */
  async stashPurged(pois: readonly Poi[]): Promise<void> {
    await runTransaction(this.database, [STORES.purgeBin, STORES.pois], 'readwrite', (transaction) => {
      const bin = transaction.objectStore(STORES.purgeBin);
      const store = transaction.objectStore(STORES.pois);
      for (const poi of pois) {
        bin.put(poi);
        store.delete(poi.id);
      }
    });
  }

  async readPurgeBin(): Promise<readonly Poi[]> {
    return readAll<Poi>(this.database, STORES.purgeBin);
  }

  /** Rétablit les POI purgés pendant la fenêtre d'annulation. */
  async restorePurged(): Promise<readonly Poi[]> {
    const pois = await this.readPurgeBin();
    await runTransaction(this.database, [STORES.purgeBin, STORES.pois], 'readwrite', (transaction) => {
      const store = transaction.objectStore(STORES.pois);
      for (const poi of pois) store.put(poi);
      transaction.objectStore(STORES.purgeBin).clear();
    });
    return pois;
  }

  /** Ferme la fenêtre d'annulation : la suppression devient définitive. */
  async commitPurge(): Promise<void> {
    await clearStores(this.database, [STORES.purgeBin]);
  }

  // -------------------------------------------------------------------------
  // Verrou de session — EF-FIC-001, CA-054
  // -------------------------------------------------------------------------

  async readLock(): Promise<StoredLock | null> {
    return readValue<StoredLock>(this.database, STORES.lock, SINGLETON_KEY);
  }

  async writeLock(lock: StoredLock): Promise<void> {
    await writeValue(this.database, STORES.lock, lock, SINGLETON_KEY);
  }

  async clearLock(): Promise<void> {
    await clearStores(this.database, [STORES.lock]);
  }

  // -------------------------------------------------------------------------
  // Réinitialisation — EF-FIC-003
  // -------------------------------------------------------------------------

  /** Efface le roadbook, sa curation et ses résultats ; conserve les réglages. */
  async resetRoadbook(): Promise<void> {
    await clearStores(this.database, [
      STORES.meta,
      STORES.route,
      STORES.pois,
      STORES.results,
      STORES.searchState,
      STORES.purgeBin,
    ]);
  }

  /** Efface la totalité du stockage local, verrou compris. */
  async resetAll(): Promise<void> {
    await clearStores(this.database, [
      STORES.meta,
      STORES.route,
      STORES.pois,
      STORES.results,
      STORES.settings,
      STORES.searchState,
      STORES.purgeBin,
      STORES.lock,
    ]);
  }
}

/**
 * Verrou de session. Plusieurs contextes de l'application peuvent partager la
 * même base : application installée et onglet du navigateur, ou deux fenêtres.
 *
 * Le rapport de trois entre la période de rafraîchissement et le délai
 * d'expiration tolère deux battements manqués avant qu'un contexte actif ne
 * soit dépossédé à tort.
 */
export class SessionLock {
  readonly contextId: string;
  private timer: ReturnType<typeof setInterval> | null = null;
  private owned = false;

  constructor(
    private readonly store: SessionStore,
    private readonly now: () => number = (): number => Date.now(),
    contextId: string = newId(),
  ) {
    this.contextId = contextId;
  }

  get isOwner(): boolean {
    return this.owned;
  }

  /** Vrai lorsque le verrou est libre ou abandonné depuis plus de 30 secondes. */
  async isAvailable(): Promise<boolean> {
    const lock = await this.store.readLock();
    if (lock === null) return true;
    if (lock.contextId === this.contextId) return true;
    return this.now() - lock.refreshedAt > LOCK_EXPIRY_MS;
  }

  /**
   * Tente de prendre le verrou. Retourne faux lorsqu'un autre contexte le
   * détient encore : l'application s'ouvre alors en lecture seule.
   */
  async acquire(force = false): Promise<boolean> {
    if (!force && !(await this.isAvailable())) {
      this.owned = false;
      return false;
    }
    await this.store.writeLock({ contextId: this.contextId, refreshedAt: this.now() });
    this.owned = true;
    return true;
  }

  /** Rafraîchit le verrou. Retourne faux si un autre contexte a pris la main. */
  async refresh(): Promise<boolean> {
    const lock = await this.store.readLock();
    if (lock !== null && lock.contextId !== this.contextId) {
      this.owned = false;
      return false;
    }
    await this.store.writeLock({ contextId: this.contextId, refreshedAt: this.now() });
    this.owned = true;
    return true;
  }

  /** Démarre le rafraîchissement périodique, tant que le document est visible. */
  start(onLost: () => void): void {
    this.stop();
    this.timer = setInterval(() => {
      void this.refresh().then((kept) => {
        if (!kept) onLost();
      });
    }, LOCK_REFRESH_MS);
  }

  /** Le rafraîchissement cesse lorsque le document passe à l'état masqué. */
  stop(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
  }

  async release(): Promise<void> {
    this.stop();
    if (!this.owned) return;
    const lock = await this.store.readLock();
    if (lock?.contextId === this.contextId) await this.store.clearLock();
    this.owned = false;
  }
}

/**
 * Ordonnanceur de sauvegarde : accumule les identifiants de POI modifiés et
 * n'écrit qu'eux, au plus une fois toutes les 5 secondes (EF-FIC-001, ENF-014).
 */
export class SaveScheduler {
  private pendingPois = new Set<PoiId>();
  private removedPois = new Set<PoiId>();
  private full = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private running: Promise<void> | null = null;
  private lastError: unknown = null;

  constructor(
    private readonly store: SessionStore,
    private readonly snapshot: () => { document: RoadbookDocument; parameters: Parameters },
    private readonly onError: (error: unknown) => void,
    private readonly delayMs: number = SAVE_COALESCE_MS,
  ) {}

  get failure(): unknown {
    return this.lastError;
  }

  /** Demande une sauvegarde complète, à l'importation d'un parcours ou d'un roadbook. */
  requestFull(): void {
    this.full = true;
    this.schedule();
  }

  /** Demande la réécriture des seuls POI cités. */
  requestPois(changed: readonly PoiId[], removed: readonly PoiId[] = []): void {
    for (const id of changed) this.pendingPois.add(id);
    for (const id of removed) {
      this.removedPois.add(id);
      this.pendingPois.delete(id);
    }
    this.schedule();
  }

  private schedule(): void {
    if (this.timer !== null) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.flush();
    }, this.delayMs);
  }

  /** Force l'écriture immédiate, au passage à l'arrière-plan par exemple. */
  async flush(): Promise<void> {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.running !== null) await this.running;
    if (!this.full && this.pendingPois.size === 0 && this.removedPois.size === 0) return;

    const full = this.full;
    const changed = [...this.pendingPois];
    const removed = [...this.removedPois];
    this.full = false;
    this.pendingPois = new Set();
    this.removedPois = new Set();

    const { document, parameters } = this.snapshot();
    this.running = (full
      ? this.store.saveAll(document, parameters)
      : this.store.savePartial(document, changed, removed)
    )
      .then(() => {
        this.lastError = null;
      })
      .catch((error: unknown) => {
        this.lastError = error;
        this.onError(error);
      })
      .finally(() => {
        this.running = null;
      });

    await this.running;
  }
}
